import { Config } from '~/api/def/endpoints/dashboardEndpoint';
import { FEATURE_FLAGS } from './orgConfigConstants';

/**
 * Shape of organization publicMetadata in Clerk.
 * Set featureFlags in Clerk Dashboard or Backend API to control which config is used.
 */
export interface ClerkOrganizationPublicMetadata {
  featureFlags?: string[];
}

export interface OrganizationFeatureFlags {
  /** Whether this organization uses dashboard-based navigation */
  useDashboardNavigation: boolean;
  /** Whether portfolio selection is required for dashboard creation */
  requirePortfolioForDashboard: boolean;
  /** Array of feature flags that are enabled for this organization */
  enableFeatures: string[];
}

export interface OrganizationConfig {
  /** Config key (feature flag name) */
  slug: string;
  /** Display name for the organization */
  name: string;
  /** Feature flags for this organization */
  features: OrganizationFeatureFlags;
  /** Default dashboard configuration */
  defaultDashboard: {
    name: string;
    description: string;
    layout: Config[];
  };
}

const portfolioManagementLayout: Config[] = [
  { h: 10, w: 4, x: 0, y: 0, id: 'proposed-portfolio', isDynamic: false },
  { h: 10, w: 8, x: 4, y: 0, id: 'risk-alerts', isDynamic: false },
  { h: 10, w: 4, x: 0, y: 10, id: 'historical-returns', isDynamic: false },
  { h: 10, w: 8, x: 4, y: 10, id: 'forward-returns', isDynamic: false },
  { h: 16, w: 12, x: 0, y: 20, id: 'sector-allocation', isDynamic: false },
];

const dynamicDashboardLayout: Config[] = [];

const fundManagementLayout: Config[] = [
  { w: 6, h: 8, x: 0, y: 0, id: 'allocation' },
  { w: 6, h: 7, x: 6, y: 0, id: 'risk-alerts' },
  { w: 6, h: 7, x: 6, y: 7, id: 'market-analysis' },
  { w: 6, h: 7, x: 6, y: 14, id: 'news-impact-analysis' },
];

const featureFlagConfigs: Record<string, OrganizationConfig> = {
  [FEATURE_FLAGS.PORTFOLIO_MANAGEMENT]: {
    slug: FEATURE_FLAGS.PORTFOLIO_MANAGEMENT,
    name: 'Portfolio Management',
    features: {
      useDashboardNavigation: true,
      requirePortfolioForDashboard: true,
      enableFeatures: [FEATURE_FLAGS.PORTFOLIO_MANAGEMENT],
    },
    defaultDashboard: {
      name: 'Visible Alpha',
      description: 'Default dashboard with standard widgets',
      layout: portfolioManagementLayout,
    },
  },
  [FEATURE_FLAGS.DYNAMIC_DASHBOARD]: {
    slug: FEATURE_FLAGS.DYNAMIC_DASHBOARD,
    name: 'Dynamic Dashboard',
    features: {
      useDashboardNavigation: true,
      requirePortfolioForDashboard: false,
      enableFeatures: [FEATURE_FLAGS.DYNAMIC_DASHBOARD],
    },
    defaultDashboard: {
      name: 'Default Dashboard',
      description: 'Default dashboard configuration',
      layout: dynamicDashboardLayout,
    },
  },
  [FEATURE_FLAGS.FUND_MANAGEMENT]: {
    slug: FEATURE_FLAGS.FUND_MANAGEMENT,
    name: 'Fund Management',
    features: {
      useDashboardNavigation: true,
      requirePortfolioForDashboard: false,
      enableFeatures: [FEATURE_FLAGS.FUND_MANAGEMENT],
    },
    defaultDashboard: {
      name: 'Example Dashboard',
      description: 'Custom dashboard for fund management',
      layout: fundManagementLayout,
    },
  },
};

const defaultConfig = featureFlagConfigs[FEATURE_FLAGS.PORTFOLIO_MANAGEMENT];

/**
 * Legacy mapping from org slug to feature flags for backward compatibility.
 * When Clerk org publicMetadata.featureFlags is set, use useFeatureFlagsFromClerk() instead.
 */
const orgSlugToFeatureFlags = (
  orgSlug: string | null | undefined,
): string[] => {
  if (!orgSlug) return [FEATURE_FLAGS.PORTFOLIO_MANAGEMENT];
  if (orgSlug === 'example-org') return [FEATURE_FLAGS.FUND_MANAGEMENT];
  return [FEATURE_FLAGS.PORTFOLIO_MANAGEMENT];
};

export type ConfigSource = string[] | string | null | undefined;

/**
 * Resolve organization config from feature flags (from Clerk org publicMetadata).
 * Uses first matching flag in order: portfolio-management, dynamic-dashboard, fund-management.
 * Falls back to portfolio-management when none match or flags are empty.
 */
export const getOrganizationConfigByFeatureFlags = (
  featureFlags: string[] | null | undefined,
): OrganizationConfig => {
  const flags = featureFlags ?? [];
  const order = [
    FEATURE_FLAGS.PORTFOLIO_MANAGEMENT,
    FEATURE_FLAGS.DYNAMIC_DASHBOARD,
    FEATURE_FLAGS.FUND_MANAGEMENT,
  ];
  for (const flag of order) {
    if (flags.includes(flag) && featureFlagConfigs[flag]) {
      return featureFlagConfigs[flag];
    }
  }
  return defaultConfig;
};

/**
 * Get organization configuration by feature flags or legacy org slug.
 * Prefer passing feature flags from useFeatureFlagsFromClerk(); org slug is supported for backward compatibility.
 */
export const getOrganizationConfig = (
  source: ConfigSource,
): OrganizationConfig => {
  if (Array.isArray(source)) {
    return getOrganizationConfigByFeatureFlags(source);
  }
  const flags = orgSlugToFeatureFlags(source);
  return getOrganizationConfigByFeatureFlags(flags);
};

/**
 * Check if a feature is enabled for the given config source (feature flags or org slug).
 * When source is an array, each flag in the array is considered enabled (supports multiple flags).
 */
export const isFeatureEnabled = (
  source: ConfigSource,
  feature: string,
): boolean => {
  if (Array.isArray(source) && source.includes(feature)) return true;
  const config = getOrganizationConfig(source);
  return config.features.enableFeatures.includes(feature);
};

/**
 * Check if organization uses dashboard navigation.
 */
export const usesDashboardNavigation = (source: ConfigSource): boolean => {
  const config = getOrganizationConfig(source);
  return config.features.useDashboardNavigation;
};

/**
 * Check if portfolio is required for dashboard creation.
 */
export const requiresPortfolioForDashboard = (
  source: ConfigSource,
): boolean => {
  const config = getOrganizationConfig(source);
  return config.features.requirePortfolioForDashboard;
};

/**
 * Get default dashboard layout for the given config source.
 */
export const getDefaultDashboardLayout = (source: ConfigSource): Config[] => {
  const config = getOrganizationConfig(source);
  return config.defaultDashboard.layout;
};

/**
 * Get default dashboard name for the given config source.
 */
export const getDefaultDashboardName = (source: ConfigSource): string => {
  const config = getOrganizationConfig(source);
  return config.defaultDashboard.name;
};

/**
 * Get default dashboard description for the given config source.
 */
export const getDefaultDashboardDescription = (
  source: ConfigSource,
): string => {
  const config = getOrganizationConfig(source);
  return config.defaultDashboard.description;
};
